import { Button, Modal, type ChartCardExportFormat } from '@insio/ui-toolkit';
import { useFeatureTranslation } from '@/hooks/useFeatureTranslation';
import { FormatCard } from '@/components/FormatCard';

interface FormatOption {
  format: ChartCardExportFormat;
  icon: 'file-lines' | 'grid-2';
  titleKey: string;
  descriptionKey: string;
}

const FORMAT_OPTIONS: FormatOption[] = [
  {
    format: 'pdf',
    icon: 'file-lines',
    titleKey: 'chartExport.formats.pdf.title',
    descriptionKey: 'chartExport.formats.pdf.description'
  },
  {
    format: 'xlsx',
    icon: 'grid-2',
    titleKey: 'chartExport.formats.xlsx.title',
    descriptionKey: 'chartExport.formats.xlsx.description'
  }
];

export interface ChartExportModalProps {
  open: boolean;
  onClose: () => void;
  /** Chart title shown in the modal subtitle. */
  chartTitle?: string;
  /** Currently exporting format, null when idle. */
  exporting: ChartCardExportFormat | null;
  hasError: boolean;
  /** Starts the export; resolves true on success. */
  onExport: (format: ChartCardExportFormat) => Promise<boolean>;
}

/**
 * Modal confirming a chart export and selecting the target format.
 * Clicking a format card starts the export and closes the modal on success.
 */
export function ChartExportModal({
  open,
  onClose,
  chartTitle,
  exporting,
  hasError,
  onExport
}: ChartExportModalProps) {
  const t = useFeatureTranslation('aiChat');
  const isExporting = exporting !== null;

  const handleFormatClick = async (format: ChartCardExportFormat) => {
    if (isExporting) return;
    const success = await onExport(format);
    if (success) onClose();
  };

  const handleClose = () => {
    if (isExporting) return;
    onClose();
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      size='sm'
      className='w-full! sm:w-150! max-h-[90vh] overflow-y-auto'
    >
      <Modal.Header
        label={t('chartExport.modal.title')}
        description={chartTitle ?? t('chartExport.modal.subtitle')}
        onClose={handleClose}
        variant='white'
      />

      <Modal.Content className='h-auto! min-h-0'>
        <div className='space-y-md'>
          {FORMAT_OPTIONS.map(option => (
            <FormatCard
              key={option.format}
              icon={option.icon}
              title={t(option.titleKey)}
              description={t(option.descriptionKey)}
              isLoading={exporting === option.format}
              isDisabled={isExporting}
              exportingLabel={t('chartExport.modal.exporting')}
              onClick={() => handleFormatClick(option.format)}
            />
          ))}

          {hasError && <Modal.Error role='alert'>{t('chartExport.modal.error')}</Modal.Error>}
        </div>
      </Modal.Content>

      <Modal.Footer variant='white'>
        <Modal.Action>
          <Button variant='ghost' size='md' onClick={handleClose} disabled={isExporting}>
            {t('chartExport.modal.cancel')}
          </Button>
        </Modal.Action>
      </Modal.Footer>
    </Modal>
  );
}
